import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, deleteDoc,
  collection, getDocs, query, where,
} from 'firebase/firestore';
import { auth, db } from './firebase';

export const ADMIN_EMAIL = 'aashityagi0627@gmail.com';

// ── AUTH ──────────────────────────────────────────────────────────────────────
export function onAuthChange(cb) {
  return onAuthStateChanged(auth, cb);
}

export async function checkHandleAvailable(handle, excludeUid = null) {
  const snap = await getDocs(query(collection(db, 'users'), where('instagramHandle', '==', handle)));
  if (snap.empty) return true;
  if (excludeUid && snap.docs.length === 1 && snap.docs[0].id === excludeUid) return true;
  return false;
}

export async function apiRegister(email, password, instagramHandle, displayName) {
  try {
    const handle = instagramHandle.replace('@', '').toLowerCase().trim();
    const available = await checkHandleAvailable(handle);
    if (!available) {
      return { ok: false, message: 'That Instagram handle is already registered. Try logging in instead.' };
    }
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;
    await setDoc(doc(db, 'users', uid), {
      uid, email,
      displayName: displayName || handle,
      instagramHandle: handle,
      approved: email === ADMIN_EMAIL,
      isAdmin: email === ADMIN_EMAIL,
      theme: null,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      lastActive: new Date().toISOString(),
      invoiceName: displayName || '',
      invoiceAddress: '',
      invoiceEmail: email,
      invoicePhone: '',
      invoiceAbn: '',
      bankName: '',
      bankBsb: '',
      bankAccount: '',
      bankAccountName: '',
      rates: [],
    });
    return { ok: true, uid };
  } catch (e) {
    return { ok: false, message: e.message };
  }
}

export async function apiLogin(email, password) {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const updateData = {
      lastLogin: new Date().toISOString(),
      lastActive: new Date().toISOString(),
    };
    // Always ensure admin is approved in Firestore
    if (email.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase().trim()) {
      updateData.approved = true;
    }
    await setDoc(doc(db, 'users', cred.user.uid), updateData, { merge: true });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: 'Wrong email or password' };
  }
}

export async function apiSignOut() { await signOut(auth); }

export async function apiForgotPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email.trim().toLowerCase());
    return { ok: true };
  } catch (e) {
    if (e.code === 'auth/user-not-found' || e.code === 'auth/invalid-email' || e.code === 'auth/invalid-credential') return { ok: true };
    return { ok: false, message: e.message };
  }
}

export async function apiChangePassword(currentPassword, newPassword) {
  try {
    const user = auth.currentUser;
    const cred = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, cred);
    await updatePassword(user, newPassword);
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e.message || 'Error changing password' };
  }
}

export async function updateLastActive(uid) {
  try {
    await setDoc(doc(db, 'users', uid), { lastActive: new Date().toISOString() }, { merge: true });
  } catch (e) { /* silent */ }
}

export async function saveUserTheme(uid, themeId) {
  await setDoc(doc(db, 'users', uid), { theme: themeId }, { merge: true });
}

// ── USER PROFILE ──────────────────────────────────────────────────────────────
export async function getUserProfile(uid) {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error('getUserProfile error:', e);
    return null;
  }
}

export async function updateUserProfile(uid, data) {
  const { photoBase64, ...rest } = data;
  if (rest.instagramHandle) {
    const handle = rest.instagramHandle.replace('@', '').toLowerCase().trim();
    const available = await checkHandleAvailable(handle, uid);
    if (!available) return { ok: false, message: 'That Instagram handle is already taken by another account.' };
    rest.instagramHandle = handle;
  }
  await setDoc(doc(db, 'users', uid), rest, { merge: true });
  return { ok: true };
}

export async function saveProfilePhoto(uid, base64) {
  await setDoc(doc(db, 'userPhotos', uid), { photoBase64: base64, updatedAt: new Date().toISOString() });
  return { ok: true };
}

export async function getProfilePhoto(uid) {
  try {
    const snap = await getDoc(doc(db, 'userPhotos', uid));
    return snap.exists() ? snap.data().photoBase64 : null;
  } catch (e) { return null; }
}

export async function getAllUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => d.data());
}

export async function approveUser(targetUid, targetEmail) {
  await setDoc(doc(db, 'users', targetUid), { approved: true }, { merge: true });
  await setDoc(doc(db, 'notifications', targetUid), {
    type: 'approved', email: targetEmail, approvedAt: new Date().toISOString(),
  });
}

// ── COLLABS ───────────────────────────────────────────────────────────────────
export async function loadCollabs(uid) {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'collabs'));
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  } catch (e) { return []; }
}

export async function saveCollab(uid, collab, instagramHandle) {
  const data = { ...collab, updatedAt: new Date().toISOString() };
  if (instagramHandle) data.creatorHandle = instagramHandle;
  await setDoc(doc(db, 'users', uid, 'collabs', collab.id), data);
  if (collab.shareId) await setDoc(doc(db, 'shares', collab.shareId), { uid, collabId: collab.id });
}

export async function deleteCollab(uid, collabId) {
  await deleteDoc(doc(db, 'users', uid, 'collabs', collabId));
}

export async function getCollabByShareId(shareId) {
  try {
    const p = await getDoc(doc(db, 'shares', shareId));
    if (!p.exists()) return null;
    const { uid, collabId } = p.data();
    const c = await getDoc(doc(db, 'users', uid, 'collabs', collabId));
    return c.exists() ? { ...c.data(), id: c.id } : null;
  } catch (e) { return null; }
}

// ── INVOICES ──────────────────────────────────────────────────────────────────
export async function loadInvoices(uid) {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'invoices'));
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  } catch (e) { return []; }
}
export async function saveInvoice(uid, invoice) {
  await setDoc(doc(db, 'users', uid, 'invoices', invoice.id), invoice);
}
export async function deleteInvoice(uid, invoiceId) {
  await deleteDoc(doc(db, 'users', uid, 'invoices', invoiceId));
}
export async function getNextInvoiceNumber(uid) {
  const snap = await getDocs(collection(db, 'users', uid, 'invoices'));
  const y = new Date().getFullYear();
  const m = String(new Date().getMonth() + 1).padStart(2, '0');
  return `INV-${y}-${m}-${String(snap.size + 1).padStart(3, '0')}`;
}

// ── CAMPAIGNS ─────────────────────────────────────────────────────────────────
export async function loadCampaigns(uid) {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'campaigns'));
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  } catch (e) { return []; }
}
export async function saveCampaign(uid, campaign) {
  await setDoc(doc(db, 'users', uid, 'campaigns', campaign.id), campaign);
}
export async function deleteCampaign(uid, campaignId) {
  await deleteDoc(doc(db, 'users', uid, 'campaigns', campaignId));
}

// ── LINKS PAGE ────────────────────────────────────────────────────────────────
export async function loadLinksPage(handle) {
  try {
    const configSnap = await getDoc(doc(db, 'links', handle));
    const itemsSnap = await getDocs(collection(db, 'links', handle, 'items'));
    const items = itemsSnap.docs.map(d => ({ ...d.data(), id: d.id }));
    items.sort((a, b) => (a.order || 0) - (b.order || 0));
    return { config: configSnap.exists() ? configSnap.data() : null, items };
  } catch (e) { return { config: null, items: [] }; }
}
export async function saveLinksConfig(handle, config) {
  const { profilePhotoBase64, ...rest } = config;
  await setDoc(doc(db, 'links', handle), rest, { merge: true });
  if (profilePhotoBase64 !== undefined) {
    await setDoc(doc(db, 'linkPhotos', handle), { profilePhotoBase64, updatedAt: new Date().toISOString() });
  }
}
export async function getLinksPhoto(handle) {
  try {
    const snap = await getDoc(doc(db, 'linkPhotos', handle));
    return snap.exists() ? snap.data().profilePhotoBase64 : null;
  } catch (e) { return null; }
}
export async function saveLinkItem(handle, item) {
  const { logoBase64, ...rest } = item;
  await setDoc(doc(db, 'links', handle, 'items', item.id), rest);
  if (logoBase64 !== undefined) {
    await setDoc(doc(db, 'linkItemPhotos', handle + '_' + item.id), { logoBase64, updatedAt: new Date().toISOString() });
  }
}
export async function getLinkItemPhoto(handle, itemId) {
  try {
    const snap = await getDoc(doc(db, 'linkItemPhotos', handle + '_' + itemId));
    return snap.exists() ? snap.data().logoBase64 : null;
  } catch (e) { return null; }
}
export async function deleteLinkItem(handle, itemId) {
  await deleteDoc(doc(db, 'links', handle, 'items', itemId));
}

// ── UTILS ─────────────────────────────────────────────────────────────────────
export function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
export function generateShareId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── CONTENT STUDIO ────────────────────────────────────────────────────────────
export async function loadContent(uid) {
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'content'));
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  } catch (e) { return []; }
}

export async function saveContent(uid, item) {
  await setDoc(doc(db, 'users', uid, 'content', item.id), {
    ...item, updatedAt: new Date().toISOString(),
  });
}

export async function deleteContent(uid, itemId) {
  await deleteDoc(doc(db, 'users', uid, 'content', itemId));
}

export async function loadHashtagSets(uid) {
  try {
    const snap = await getDoc(doc(db, 'users', uid, 'settings', 'hashtags'));
    return snap.exists() ? snap.data().sets : null;
  } catch (e) { return null; }
}

export async function saveHashtagSets(uid, sets) {
  await setDoc(doc(db, 'users', uid, 'settings', 'hashtags'), { sets });
}
